import * as JSURL from '@yaska-eu/jsurl2';
import pako from 'pako';
import {Base64} from 'js-base64';
import {FilterValue, ReportFilters} from 'app/features/analytics/service/filter-value.interface';


function numericToInt (s) {
    return isNumeric(s) ? Number(s) : s;
}

function convertFilterFromBase64 (f: FilterValue) {
    if (typeof f.value !== 'string') {
        return;
    }

    try {
        let toUnit = Base64.toUint8Array(String(f.value));
        let toInflate = pako.inflate(toUnit, {to: 'string'});

        f.value = toInflate.split('&');
    } catch (e) {
        f.value = [];
    }
}

function filterBase64Id (id: string) {
    return id.includes('p_') ? id.slice(2, id.length) : id;
}

export function packFiltersArrays (values: Record<any, any>[]) {
    if (Array.isArray(values)) {
        if (values.length) {
            return values.reduce((acc, f) => {
                let {id, value, ...rest} = f;
                if (rest.mode !== undefined && !rest.mode) {
                    delete rest.mode;
                }

                if (rest.search !== undefined && !rest.search) {
                    delete rest.search;
                }

                value = Array.isArray(value) ? value.map(numericToInt) : numericToInt(value);

                if (Object.keys(rest).length) {
                    if (id.includes('ATTRIBUTION') || id.includes('LINE')) {
                        const compressed = pako.deflate(value.join('&'));
                        const base64UrlSafe = Base64.fromUint8Array(compressed, true);

                        acc[`p_${id}`] = {
                            ...rest,
                            value: value.length ? base64UrlSafe : []
                        };
                    } else {
                        acc[id] = {
                            ...rest,
                            value
                        };
                    }
                } else {
                    // just values
                    acc[id] = value;
                }

                return acc;
            }, {});
        }
    }
}

export function unPackFiltersArrays (values: Record<any, any>): Record<string, any>[] {
    if (!values || typeof values !== 'object') {
        return [];
    }
    const result = [];
    Object.entries(values).forEach(([id, rest]) => {
        if ((id.includes('ATTRIBUTION') || id.includes('LINE')) && id.includes('p_')) {
            convertFilterFromBase64(rest);
        }

        if (Array.isArray(rest)) {
            if (rest.some(x => typeof x !== 'string' && typeof x !== 'number')) {
                // bad record
                return;
            }
            // just values
            return result.push({
                value: rest.map(String),
                id: filterBase64Id(id)
            });
        }
        if (rest && typeof rest === 'object') {
            if (!Array.isArray(rest.value) || rest.value.some(x => typeof x !== 'string' && typeof x !== 'number')) {
                // custom record
                return result.push({
                    ...rest,
                    id: filterBase64Id(id)
                });
            }
            return result.push({
                ...rest,
                value: Array.isArray(rest) ? rest.map(String) : rest.value.map(String),
                id: filterBase64Id(id)
            });
        }
    });
    return result;
}

export function packGroupArrays (values: Record<any, any>[]) {
    if (Array.isArray(values)) {
        if (values.length) {
            return values.reduce((acc, f) => {
                const {id, value, ...rest} = f;
                if (Object.keys(rest).length) {
                    acc.push(f);
                } else {
                    // just values
                    acc.push(id);
                }
                return acc;
            }, []);
        }
    }
}

export function unPackGroupArrays (values: Record<any, any>[]) {
    if (!values || typeof values !== 'object') {
        return [];
    }
    const result = [];
    (values).forEach(rest => {
        if (typeof rest === 'string' || typeof rest === 'number') {
            // just values
            return result.push({
                id: String(rest)
            });
        }
        if (rest && typeof rest === 'object') {
            return result.push({
                ...rest
            });
        }
    });
    return result;
}

export function isNumeric (str: string): boolean {
    return Number(str).toString(10) === str;
}


export function skipEmptyValues<T extends {}> (orig: T): Partial<T> {
    return Object
        .entries(orig)
        .filter(([_, value]) =>
            !(
                value === undefined
                || value === null
                || (Array.isArray(value) && value.length === 0)
                || (typeof value === 'object' && Object.keys(value).length === 0)
            )
        )
        .reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
        }, {});

}

interface UnPackedResult<T> {
    result: T;
    parsedValue: Record<string, any>;
}

export function unpackFilterAndGroups<T extends ReportFilters> (initialValue: T, queryParams): UnPackedResult<T> {
    const result = {...initialValue};
    const parsedValue = JSURL.parse(queryParams.q ? queryParams.q : '');

    result.groupBy = unPackGroupArrays(parsedValue.g);
    result.filter = unPackFiltersArrays(parsedValue.f) as FilterValue[];

    if (parsedValue.u && String(parsedValue.u).trim().length) {
        (result as any).userId = String(parsedValue.u).trim();
    }

    return {result, parsedValue};
}

export function packQueryParams<T extends {}> (obj: T): { q: string } {
    return {q: JSURL.stringify(skipEmptyValues(obj))};
}
